/**
 * crewModel — pure helpers for the CrewSlotEditor (BON-200). Keeps the draft
 * logic (grouping by role, diffing draft vs. saved, building the PUT payload)
 * out of the component so it's unit-testable on its own.
 *
 * A crew member in the local draft is a {@link DraftMember}: the userId, a
 * resolved display name (for the row label + avatar), and the role slot it
 * fills. The PUT body is the full desired set mapped to `{ userId, position }`.
 */
import type { CrewPosition, CrewSlotInput, WorkshopCrewMember } from '../../../../api/workshops';
import {
  CREW_POSITION_LABELS,
  CREW_POSITION_NOUNS,
  CREW_POSITIONS,
} from '../../../../lib/crewPositions';

/** The three role sections, in display order. */
export const CREW_ROLES: CrewPosition[] = [...CREW_POSITIONS];

export interface RoleConfig {
  position: CrewPosition;
  /** Section header label, e.g. "TEACHER". */
  label: string;
  /** Singular noun for the Add button, e.g. "teacher". */
  noun: string;
  /** Teacher is required + capped at one; the others are optional + unlimited. */
  required: boolean;
  /** Max slots; teacher = 1, others = Infinity. */
  max: number;
}

/** Slot RULES per position. Labels and nouns come from `lib/crewPositions`
 *  (BON-420) — only `required` and `max` are the crew editor's own concern. */
export const ROLE_CONFIG: Record<CrewPosition, RoleConfig> = {
  teacher: {
    position: 'teacher',
    label: CREW_POSITION_LABELS.teacher,
    noun: CREW_POSITION_NOUNS.teacher,
    required: true,
    max: 1,
  },
  assistant: {
    position: 'assistant',
    label: CREW_POSITION_LABELS.assistant,
    noun: CREW_POSITION_NOUNS.assistant,
    required: false,
    max: Infinity,
  },
  teacher_in_training: {
    position: 'teacher_in_training',
    label: CREW_POSITION_LABELS.teacher_in_training,
    noun: CREW_POSITION_NOUNS.teacher_in_training,
    required: false,
    max: Infinity,
  },
};

/** One staged crew member in the local draft. */
export interface DraftMember {
  userId: string;
  name: string;
  position: CrewPosition;
}

/** Resolve a crew member's display name (prefer displayName, else first+last). */
export function memberName(m: {
  displayName?: string | null;
  firstName?: string;
  lastName?: string;
}): string {
  const full = `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim();
  return (m.displayName && m.displayName.trim()) || full || 'Unknown';
}

/** Build the initial draft from the workshop's saved crew. Members with an
 *  unrecognized position are dropped (the editor only manages the three known
 *  roles). */
export function draftFromCrew(staff: WorkshopCrewMember[]): DraftMember[] {
  return staff
    .filter((m): m is WorkshopCrewMember & { position: CrewPosition } =>
      (CREW_ROLES as string[]).includes(m.position),
    )
    .map((m) => ({ userId: m.userId, name: memberName(m), position: m.position }));
}

/** The draft members for a given role, in insertion order. */
export function membersForRole(draft: DraftMember[], position: CrewPosition): DraftMember[] {
  return draft.filter((m) => m.position === position);
}

/** Whether another person can be added to a role given the draft. */
export function canAddRole(draft: DraftMember[], position: CrewPosition): boolean {
  return membersForRole(draft, position).length < ROLE_CONFIG[position].max;
}

/** The PUT /:id/staff body — the full desired crew set. */
export function toPayload(draft: DraftMember[]): CrewSlotInput[] {
  return draft.map((m) => ({ userId: m.userId, position: m.position }));
}

/** A stable signature of a crew set (role + userId), order-independent, so a
 *  draft that only reorders nothing still compares equal to the saved set. */
function signature(members: { userId: string; position: CrewPosition }[]): string {
  return members
    .map((m) => `${m.position}:${m.userId}`)
    .sort()
    .join('|');
}

/** True when the draft differs from the saved crew (drives the unsaved bar). */
export function isDirty(draft: DraftMember[], saved: WorkshopCrewMember[]): boolean {
  return signature(draft) !== signature(draftFromCrew(saved));
}

/** The crew picker's region label for a member, in all-staff mode only. */
export function regionTag(
  member: { id: string; regions?: Array<{ id: string }> },
  inRegionIds: ReadonlySet<string> | null,
): string | null {
  if (inRegionIds === null) return null;
  if ((member.regions ?? []).length === 0) return 'No region';
  return inRegionIds.has(member.id) ? null : 'Outside region';
}
